// ============================================
// FILE: src/utils/mediaHelpers.js
// ============================================
//
// Normalises whatever media the API returned into one ordered list the fashion
// gallery can walk, and resolves the right URL for each slot.
//
// All URL resolution delegates to resolveProductImageUrl from imageHelpers —
// it already handles absolute URLs, /uploads/, /storage/, data: and blob:, and
// duplicating that logic here would guarantee the two drift apart.

import { resolveProductImageUrl } from './imageHelpers';

/**
 * Parse a YouTube or Vimeo URL client-side.
 * The backend normally does this at save time and sends embed_url/poster_url
 * back, so this is only a fallback for rows written before that existed.
 */
export const parseVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  const youtube = url.match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/))([A-Za-z0-9_-]{11})/i
  );
  if (youtube) {
    const id = youtube[1];
    return {
      provider: 'youtube',
      id,
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1&modestbranding=1`,
      posterUrl: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    };
  }

  const vimeo = url.match(/vimeo\.com\/(?:video\/|channels\/[\w]+\/|groups\/[\w]+\/videos\/)?(\d{6,})/i);
  if (vimeo) {
    const id = vimeo[1];
    return {
      provider: 'vimeo',
      id,
      embedUrl: `https://player.vimeo.com/video/${id}?playsinline=1&dnt=1`,
      posterUrl: null,
    };
  }

  return null;
};

export const isVideo = (media) => media?.media_type === 'video';

/** Full-size source for the main stage. */
export const getMediaFull = (media) => {
  if (!media) return null;
  if (isVideo(media)) return resolveProductImageUrl(media.video_url || media.url);

  return resolveProductImageUrl(media.large_url || media.url || media.image_url || media.poster_url);
};

/**
 * Highest-detail source, for the zoom view.
 *
 * The gallery stage and the lightbox have very different needs: the stage is
 * ~600px wide, the lightbox zooms past 100%. Serving the stage's image to the
 * zoom looks soft, and serving the zoom's image to the stage wastes bandwidth
 * on every page view.
 */
export const getMediaZoom = (media) => {
  if (!media) return null;
  if (isVideo(media)) return resolveProductImageUrl(media.video_url || media.url);

  return resolveProductImageUrl(
    media.xl_url || media.large_url || media.url || media.image_url
  );
};

/**
 * srcset for an image, so the browser downloads a file sized for the slot.
 * Null when the backend has no derivatives (remote or legacy images), which
 * tells the caller to fall back to a plain src.
 */
export const getMediaSrcSet = (media) => {
  if (!media || isVideo(media)) return undefined;
  return media.srcset || undefined;
};

/** Small source for the thumbnail rail. */
export const getMediaThumb = (media) => {
  if (!media) return null;

  return resolveProductImageUrl(
    media.thumbnail_url || media.poster_url || media.medium_url || media.url || media.image_url
  );
};

/** The still painted before a video is played. */
export const getMediaPoster = (media) => {
  if (!media) return null;

  const explicit = resolveProductImageUrl(media.poster_url || media.thumbnail_url || media.image_url);
  if (explicit) return explicit;

  return parseVideoUrl(media.video_url || media.url)?.posterUrl ?? null;
};

/** Iframe src for a hosted video, with autoplay appended when requested. */
export const getEmbedUrl = (media, { autoplay = false } = {}) => {
  if (!media || !isVideo(media)) return null;
  if (media.provider === 'upload') return null;

  const base = media.embed_url || parseVideoUrl(media.video_url || media.url)?.embedUrl;
  if (!base) return null;
  if (!autoplay) return base;

  return `${base}${base.includes('?') ? '&' : '?'}autoplay=1`;
};

/**
 * Build one ordered media list from whatever shape the API gave us.
 *
 * Falls back through product.media -> productImages -> images[] ->
 * featured_image, so a fashion screen opened on a product fetched by an older
 * endpoint still shows something rather than an empty stage.
 */
export const normalizeMedia = (product) => {
  if (!product) return [];

  if (Array.isArray(product.media) && product.media.length > 0) {
    return [...product.media].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }

  const legacyRows = Array.isArray(product.product_images)
    ? product.product_images
    : Array.isArray(product.productImages)
      ? product.productImages
      : null;

  if (legacyRows && legacyRows.length > 0) {
    return legacyRows
      .map((row, index) => ({
        id: row.id ?? `legacy-${index}`,
        media_type: row.media_type || 'image',
        provider: row.provider ?? null,
        sort_order: row.sort_order ?? index,
        url: row.image_url,
        image_url: row.image_url,
        thumbnail_url: row.thumbnail_url,
        poster_url: row.poster_url ?? null,
        video_url: row.video_url ?? null,
        embed_url: row.embed_url ?? null,
        alt_text: row.alt_text ?? product.name,
        attribute_value_id: row.attribute_value_id ?? null,
        option_value: row.option_value ?? null,
      }))
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  // `products.images` has meant two different things historically: a flat array
  // of URLs (seeders) and a {original, thumbnail, ...} size map (admin uploads).
  const rawImages = product.images;
  const urls = Array.isArray(rawImages)
    ? rawImages
    : rawImages && typeof rawImages === 'object'
      ? [rawImages.original].filter(Boolean)
      : [];

  const fallback = urls.length > 0
    ? urls
    : [product.featured_image_url || product.image_url || product.featured_image].filter(Boolean);

  return fallback.map((url, index) => ({
    id: `img-${index}`,
    media_type: 'image',
    provider: null,
    sort_order: index,
    url,
    image_url: url,
    thumbnail_url: url,
    poster_url: null,
    alt_text: product.name,
    attribute_value_id: null,
    option_value: null,
  }));
};

/**
 * Media scoped to one option value — normally the selected colourway.
 *
 * When that colour has no media of its own, the full list is returned rather
 * than an empty gallery: an unphotographed colour should still show the
 * product, not a blank stage.
 */
export const filterMediaByValue = (media, attributeValueId) => {
  const all = Array.isArray(media) ? media : [];
  if (attributeValueId == null) return all;

  const scoped = all.filter(
    (m) => String(m.attribute_value_id) === String(attributeValueId)
  );

  if (scoped.length === 0) return all;

  // Videos are usually not colour-specific; keep the unassigned ones so the
  // shopper can still reach them from any colourway.
  const shared = all.filter((m) => m.attribute_value_id == null);

  return [...scoped, ...shared].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
};
